import { LinkProvider } from '@remotely/ui-kit';
import { wrapCreateBrowserRouter } from '@sentry/react';
import type { ComponentType } from 'react';
import {
    createBrowserRouter,
    Navigate,
    Outlet,
    type RouteObject,
    type RouterState,
    useNavigate,
    useRouteError,
} from 'react-router';
import {
    customerCompanyView,
    customerContractors,
    customerDeposits,
    customerDocuments,
    customerEquipment,
    customerEquipmentApprove,
    customerExpenses,
    customerInvoices,
    customerTimeOff,
} from '~/acl';
import { protectedRoute, type ProtectedRouteArgs, Restricted } from '~/auth';
import ErrorPage from '~/auth/ErrorPage';
import { isIncompleteProfileBuilder } from '~/candidates/incompleteProfile';
import { DefaultLayout } from '~/components/layout';
import OpenGraphTags from '~/components/layout/OpenGraphTags';
import { MarketingProvider } from '~/marketing';
import { isLocal, isProduction } from '~/utils/env';
import { DatepickerPortal } from './components/ui/forms/DatePicker';
import { SelectPortal } from './components/ui/forms/Select';
import { ModalProvider } from './components/ui/Modal';
import RouterLink from './components/ui/RouterLink';
import { ToastProvider } from './components/ui/Toast';
import { TooltipProvider } from './components/ui/Tooltip';
import { AuthUser } from '~/auth/user.ts';

export const ROUTES = [
    {
        path: '/',
        element: <Root />,
        children: [
            { path: '/error', element: <ErrorPage /> },
            { path: '/login', ...lazyRoute(() => import('./auth/Login'), 'Login') },
            { path: '/logout', ...lazyRoute(() => import('./auth/Logout'), 'Logout') },
            { path: '/auth', ...lazyRoute(() => import('./auth/Authorize'), 'Authorize') },
            { path: '/manage/*', ...lazyRoute(() => import('./manage/index')) },
            { path: '/documents/*', ...lazyRoute(() => import('./documents/index')) },
            {
                path: '/submit-reference/:id',
                ...lazyRoute(() => import('./references/ProvideReference'), 'ProvideReference'),
            },
            {
                path: '/c/:id',
                ...lazyRoute(() => import('./candidates/PublicProfile'), 'PublicProfile'),
            },
            {
                path: '/company/:id',
                ...lazyRoute(() => import('./opportunities/Company'), 'CompanyRoot'),
            },
            {
                path: '/position/:id/:tab?',
                ...lazyRoute(() => import('./opportunities/Opening'), 'OpeningRoot'),
            },
            {
                path: '/apply/about/ai',
                ...lazyRoute(() => import('./apply/AIInterview'), 'AIInterviewAbout'),
            },
            {
                path: '/about/background-checks',
                ...lazyProtectedRoute(
                    () => import('./about/BackgroundChecks'),
                    'BackgroundChecks',
                    (user: AuthUser) => user.authenticated,
                ),
            },
            {
                path: '/about/health-insurance',
                ...lazyProtectedRoute(
                    () => import('./about/HealthInsurance'),
                    'HealthInsurance',
                    (user: AuthUser) => user.authenticated,
                ),
            },
            {
                path: '/apply/*',
                ...lazyProtectedRoute(
                    () => import('./apply/index'),
                    'Apply',
                    (user: AuthUser) => !user.authenticated || user.isApplicant(),
                ),
            },
            {
                path: '/startup/apply/*',
                ...lazyProtectedRoute(
                    () => import('./apply/startup/index'),
                    'StartupApply',
                    (user: AuthUser) => !user.authenticated || user.isCustomerLead(),
                ),
            },
            {
                path: '/r/:token',
                ...lazyProtectedRoute(
                    () => import('./apply/startup/index'),
                    'RedirectStartupApply',
                    (user: AuthUser) => !user.authenticated,
                ),
            },
            {
                path: '/offers/*',
                ...lazyRoute(() => import('./offers/index')),
            },
            {
                element: (
                    <Restricted
                        rules={[
                            {
                                is: (user: AuthUser) => user.isCustomerLead(),
                                redirectTo: '/startup/apply',
                            },
                            { is: (user: AuthUser) => user.isApplicant(), redirectTo: '/apply' },
                            { is: (user: AuthUser) => !user.authenticated, redirectTo: '/login' },
                        ]}>
                        <DefaultLayout />
                    </Restricted>
                ),
                children: [
                    {
                        path: '/admin/*',
                        ...lazyProtectedRoute(
                            () => import('./admin/index'),
                            'AdminRoot',
                            (user: AuthUser) => user.isRemotely(),
                        ),
                    },
                    {
                        path: '/references/*',

                        ...lazyRoute(() => import('./references/index')),
                    },
                    {
                        path: '/agreement/*',

                        ...lazyRoute(() => import('./agreements/index')),
                    },
                    {
                        path: '/invoices/*',

                        ...lazyProtectedRoute(
                            () => import('./invoices/index'),
                            'InvoicesRoot',
                            (user: AuthUser) => user.isContractor() || user.hasInvoices(),
                        ),
                    },
                    {
                        path: '/timeoff/*',

                        ...lazyProtectedRoute(
                            () => import('./timeoff'),
                            'TimeOffRoot',
                            (user: AuthUser) => user.isContractor() && user.hasBenefit('pto'),
                        ),
                    },
                    {
                        path: '/expenses/*',

                        ...lazyProtectedRoute(
                            () => import('./expenses/index'),
                            'ExpensesRoot',
                            (user: AuthUser) => user.isContractor(),
                        ),
                    },
                    {
                        path: '/check-in/:id',
                        ...lazyProtectedRoute(
                            () => import('./checkins/ContractorCheckin'),
                            'default',
                            (user: AuthUser) => user.isContractor(),
                        ),
                    },
                    {
                        path: '/check-in/:id/videoask',
                        ...lazyProtectedRoute(
                            () => import('./checkins/ContractorCheckinVideoAsk'),
                            'default',
                            (user: AuthUser) => user.isContractor(),
                        ),
                    },
                    {
                        path: '/customer/*',
                        element: protectedRoute(<Outlet />, (user?: AuthUser) =>
                            Boolean(user?.isCustomer()),
                        ),
                        children: [
                            {
                                path: 'checkins/*',
                                ...lazyRoute(
                                    () => import('./checkins/index'),
                                    'CustomerCheckinsRoot',
                                ),
                            },
                            {
                                path: 'contractors/*',
                                ...lazyProtectedRoute(
                                    () => import('./contractors/index'),
                                    'ContractorsRoot',
                                    customerContractors,
                                ),
                            },
                            {
                                path: 'equipment/*',

                                ...lazyProtectedRoute(
                                    () => import('./equipment/index'),
                                    'EquipmentCustomerRoot',
                                    customerEquipment,
                                    customerEquipmentApprove,
                                ),
                            },
                            {
                                path: 'documents/*',

                                ...lazyProtectedRoute(
                                    () => import('./documents/Customer'),
                                    'CustomerDocumentsRoot',
                                    customerDocuments,
                                ),
                            },
                            {
                                path: 'invoices/*',
                                ...lazyProtectedRoute(
                                    () => import('./invoices/index'),
                                    'InvoicesCustomerRoot',
                                    customerInvoices,
                                ),
                            },
                            {
                                path: 'timeoff/*',
                                ...lazyProtectedRoute(
                                    () => import('./timeoff'),
                                    'TimeOffCustomerRoot',
                                    customerTimeOff,
                                ),
                            },
                            {
                                path: 'expenses/*',
                                ...lazyProtectedRoute(
                                    () => import('./expenses/index'),
                                    'ExpensesCustomerRoot',
                                    customerExpenses,
                                ),
                            },
                            {
                                path: 'deposits/*',
                                ...lazyProtectedRoute(
                                    () => import('./deposits/index'),
                                    'DepositsRoot',
                                    customerDeposits,
                                ),
                            },
                        ],
                    },
                    {
                        path: '/customer/company/*',
                        ...lazyProtectedRoute(
                            () => import('./company/index'),
                            'CustomerCompanyRoot',
                            customerCompanyView,
                        ),
                    },
                    {
                        path: '/candidates',
                        element: (
                            <Navigate
                                to='/admin/candidates'
                                replace
                            />
                        ),
                    },
                    { path: '/candidates/*', ...lazyRoute(() => import('./candidates/index')) },
                    {
                        path: '/job_positions/*',
                        ...lazyProtectedRoute(
                            () => import('./job_positions/index'),
                            'JobPositionRoot',
                            (user: AuthUser) => user.isCustomer() || user.isRemotely(),
                        ),
                    },
                    { path: '/referrals/*', ...lazyRoute(() => import('./referrals/index')) },
                    { path: '/profile/*', ...lazyRoute(() => import('./profile/index')) },
                    {
                        path: '/opportunities/*',
                        ...lazyRoute(() => import('./opportunities/index')),
                    },
                    {
                        path: '/applications/:id/:tab?',
                        ...lazyProtectedRoute(
                            () => import('./opportunities/Opening'),
                            'OpeningRoot',
                            (user: AuthUser) => user.isCandidate() || user.isContractor(),
                        ),
                    },
                    {
                        path: '/applications/*',
                        ...lazyProtectedRoute(
                            () => import('./applications/index'),
                            'ApplicationsRoot',
                            (user: AuthUser) =>
                                (user.isCandidate() || user.isContractor()) &&
                                (user.profile_complete || isIncompleteProfileBuilder(user)),
                        ),
                    },
                    {
                        path: '/tasks/*',
                        ...lazyProtectedRoute(
                            () => import('./tasks/index'),
                            'TasksRoot',
                            (user: AuthUser) => user.isRemotely(),
                        ),
                    },
                    { path: '/settings/:tab?', ...lazyRoute(() => import('./settings/index')) },
                    {
                        path: '/home/*',
                        ...lazyRoute(() => import('./home/index')),
                    },
                    {
                        path: '/recordings/*',
                        ...lazyProtectedRoute(
                            () => import('./recordings/index'),
                            'RecordingsRoot',
                            (user: AuthUser) => user.isRemotely() || user.isCustomer(),
                        ),
                    },
                    {
                        path: '/insurance/*',
                        ...lazyProtectedRoute(
                            () => import('./insurance/index'),
                            'InsuranceRoot',
                            (user: AuthUser) => user.isContractor(),
                        ),
                    },
                    {
                        path: 'dev/*',
                        ...lazyProtectedRoute(
                            () => import('./dev/index'),
                            'DevRoot',
                            (user: AuthUser) => !isProduction() || user.isAdmin(),
                        ),
                    },
                    {
                        index: true,
                        element: (
                            <Navigate
                                to='/home'
                                replace
                            />
                        ),
                    },
                ],
            },

            /* This route has to be the last */
            {
                path: '*',
                element: (
                    <Navigate
                        to='/'
                        replace
                    />
                ),
            },
        ],
    },
];
export const ROUTES_TERMS_NOT_ACCEPTED = [
    {
        path: '/',
        element: <Root />,
        children: [
            { path: '/logout', ...lazyRoute(() => import('./auth/Logout'), 'Logout') },
            { path: '/auth', ...lazyRoute(() => import('./auth/Authorize'), 'Authorize') },
            {
                path: '*',
                ...lazyRoute(() => import('./Terms'), 'TermsRoot'),
            },
            {
                index: true,
                element: (
                    <Navigate
                        to='/terms' // it'll be caught by path: '*'
                        replace
                    />
                ),
            },
        ],
    },
];

const sentryCreateBrowserRouter = wrapCreateBrowserRouter(createBrowserRouter);

export const ROUTER = sentryCreateBrowserRouter(ROUTES);

export const ROUTER_TERMS_NOT_ACCEPTED = sentryCreateBrowserRouter(ROUTES_TERMS_NOT_ACCEPTED);

function Root() {
    return (
        <LinkProvider component={RouterLink}>
            <ToastProvider>
                <ModalProvider>
                    <OpenGraphTags />
                    <MarketingProvider>
                        <Outlet />
                    </MarketingProvider>
                    <DatepickerPortal />
                </ModalProvider>

                <TooltipProvider />
                <TooltipProvider
                    id='clickable'
                    clickable
                />
                <SelectPortal />
            </ToastProvider>
        </LinkProvider>
    );
}

// store the last pending target to redirect in case of chunk error
let lastTarget = '';

function handleNavState(state: RouterState) {
    const loc = state.navigation?.location;
    lastTarget = loc ? `${loc.pathname}${loc.search}${loc.hash}` : '';
}

ROUTER.subscribe(handleNavState);
ROUTER_TERMS_NOT_ACCEPTED.subscribe(handleNavState);

function isChunkError(error: unknown) {
    return (
        error instanceof Error && /ChunkLoadError|Loading chunk|imported module/.test(error.message)
    );
}

function ChunkErrorBoundary() {
    const error = useRouteError();
    const navigate = useNavigate();

    if (isChunkError(error)) {
        if (lastTarget) {
            void navigate(lastTarget, { replace: true });
            return null;
        }

        // fallback
        if (!isLocal()) {
            window.location.reload();
            return null;
        }
    }

    // not a chunk error, bubble up to parent error boundary
    throw error;
}

type LazyModule = Record<string, unknown>;

function lazyRoute(importer: () => Promise<LazyModule>, rootName = 'Root') {
    return {
        lazy: async () => {
            const module = await importer();
            const DynamicRoot = module[rootName] as ComponentType | undefined;
            if (!DynamicRoot) {
                throw new Error(`No component ${rootName} exported from lazy route`);
            }
            return {
                Component: DynamicRoot,
            };
        },
        lazyChildren: async () => {
            const module = await importer();
            return (module.routes ?? module[`routes${rootName}`]) as RouteObject[] | undefined;
        },
        errorElement: <ChunkErrorBoundary />,
    };
}

function lazyProtectedRoute(
    importer: () => Promise<LazyModule>,
    rootName = 'Root',
    ...args: ProtectedRouteArgs
) {
    return {
        lazy: async () => {
            const module = await importer();
            const DynamicRoot = module[rootName] as ComponentType | undefined;
            if (!DynamicRoot) {
                throw new Error(`No component ${rootName} exported from lazy route`);
            }
            return {
                Component: () => protectedRoute(<DynamicRoot />, ...args),
            };
        },
        lazyChildren: async () => {
            const module = await importer();
            return (module.routes ?? module[`routes${rootName}`]) as RouteObject[] | undefined;
        },
        errorElement: <ChunkErrorBoundary />,
    };
}
